"use client";
import { useTranslations } from "next-intl"
import { EmptyState } from "@/components/ui/empty-state"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { useRouter } from "next/navigation";
import { ChevronLeft, ChevronDown, Loader2, AlertTriangle, Pencil, X, Plus, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { GradeRangeFilterPanel } from "@/components/feed/grade-range-filter-panel";
import { createClient } from "@/utils/supabase/client";
import { getTaxonomyItems } from "@/lib/taxonomy-cache";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ItemCard } from "@/components/search/item-card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ROUTE_HEADER_SHELL_CLASS } from "@/lib/route-layout";
import { ItemCardSkeletonGrid } from "@/components/search/item-card-skeleton";
import {
  sortDiscoverListings,
  categoryHasAuctions,
  type CategorySortMode,
  type DiscoverMockListing,
} from "@/components/feed/mockDiscoverData";
import { usePaginatedFeed, type FeedFilterState, type ListingItem } from "@/hooks/use-paginated-feed";
import { liveCategoryHasAuctions, sortLiveDiscoverListings } from "@/lib/discover-category-sort";
import { discoverTypeLabelFromListing } from "@/lib/discover-listing-label";
import {
  listingFormatFilterChipActiveClasses,
  listingFormatFilterChipInactiveClasses,
} from "@/lib/listing-format-filter-styles";
import { DiscoverCategoryIcon } from "@/components/feed/discover-category-icon";
import { getCardLanguageFlag } from "@/lib/flags";

const PAGE_SIZE = 20;

const CATEGORY_SHELL =
  "mx-auto w-full max-w-(--breakpoint-lg) md:max-w-7xl xl:max-w-[min(100%,90rem)] flex-1 px-4 pt-[calc(var(--discover-category-header-height,7.5rem)+0.75rem)] md:px-6 lg:px-8 md:pt-[calc(var(--discover-category-header-height,7.5rem)+1rem)]";
const CATEGORY_GRID =
  "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-4";
const SORT_OPTIONS: { id: CategorySortMode; label: string; needsAuctions?: boolean }[] = [
  { id: "recommended", label: "recommended" },
  { id: "newest", label: "newest" },
  { id: "price-asc", label: "priceLowHigh" },
  { id: "price-desc", label: "priceHighLow" },
  { id: "ending-soon", label: "endingSoon", needsAuctions: true },
];

const FORMAT_OPTIONS: { id: string; label: string }[] = [
  { id: "fixed_price", label: "buyNow" },
  { id: "fixed_price_with_offers", label: "buyNowOffers" },
  { id: "offers_only", label: "offersOnly" },
  { id: "auction", label: "auction" },
];

interface RelativeFilterOption {
  id: string;
  name: string;
  slug?: string | null;
}

function CategoryHeader({
  categorySlug,
  name,
  sortMode,
  setSortMode,
  showEndingSoon,
  selectedFormats,
  onToggleFormat,
  onBack,
  compactGutter,
  middleContent,
  centerMiddleRow,
}: {
  categorySlug: string;
  name: string;
  sortMode: CategorySortMode;
  setSortMode: (m: CategorySortMode) => void;
  showEndingSoon: boolean;
  selectedFormats?: string[];
  onToggleFormat?: (id: string) => void;
  onBack: () => void;
  compactGutter?: boolean;
  middleContent?: ReactNode;
  centerMiddleRow?: boolean;
}) {
  const tSort = useTranslations("sort");
  const tFormat = useTranslations("listingFormat");
  const headerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => {
      root.style.setProperty("--discover-category-header-height", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--discover-category-header-height");
    };
  }, []);

  const headerChrome = compactGutter ? "md:pr-4" : "md:pr-20";

  return (
    <header
      ref={headerRef}
      className={cn(
        "fixed top-0 left-0 right-0 z-50 safe-area-top pointer-events-none isolate",
        headerChrome
      )}
      data-discover-category-header
    >
      <div
        aria-hidden
        style={{
          maskImage: "linear-gradient(to bottom, #000 0%, #000 calc(100% - 0.625rem), transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 calc(100% - 0.625rem), transparent 100%)",
          maskSize: "100% 100%",
          WebkitMaskSize: "100% 100%",
          maskRepeat: "no-repeat",
          WebkitMaskRepeat: "no-repeat",
        }}
        className="absolute inset-0 z-0 bg-linear-to-b from-background/82 via-background/42 to-transparent backdrop-blur-md dark:from-background/78 dark:via-background/36 pointer-events-none"
      />
      <div className="relative z-1">
        <div
          className={cn(
            cn(ROUTE_HEADER_SHELL_CLASS, "flex gap-2 pointer-events-auto"),
            middleContent
              ? centerMiddleRow
                ? "min-h-14 items-center py-2"
                : "min-h-14 items-start py-2.5"
              : "h-14 items-center"
          )}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-10 w-10 shrink-0 rounded-full hover:bg-muted/80 dark:hover:bg-white/10",
              middleContent && !centerMiddleRow ? "mt-0.5" : ""
            )}
            onClick={onBack}
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          {middleContent ? (
            <div className="min-w-0 flex-1">{middleContent}</div>
          ) : (
            <>
              <DiscoverCategoryIcon slug={categorySlug} className="h-5 w-5 shrink-0 text-foreground" />
              <h1 className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground drop-shadow-sm md:text-lg">
                {name}
              </h1>
            </>
          )}
        </div>
        <div
          className={cn(
            cn(ROUTE_HEADER_SHELL_CLASS, "scrollbar-hide overflow-x-auto pb-3 pt-0.5 pointer-events-auto")
          )}
        >
          <div className="flex w-max min-w-full gap-2">
            {SORT_OPTIONS.filter((o) => !o.needsAuctions || showEndingSoon).map((opt) => (
              <Button
                key={opt.id}
                type="button"
                variant={sortMode === opt.id ? "default" : "outline-solid"}
                size="sm"
                className={cn(
                  "h-8 shrink-0 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-all duration-200 border shadow-sm",
                  sortMode === opt.id
                    ? ""
                    : "border-border/40 bg-background/35 text-foreground backdrop-blur-md hover:bg-muted/60 dark:border-white/12 dark:bg-white/5 dark:hover:bg-white/10"
                )}
                onClick={() => setSortMode(opt.id)}
              >
                {tSort(opt.label as any)}
              </Button>
            ))}
            {onToggleFormat && (
              <>
                <div className="mx-0.5 h-8 w-px shrink-0 self-center bg-border/40 dark:bg-white/12" />
                {FORMAT_OPTIONS.map((opt) => {
                  const active = selectedFormats?.includes(opt.id) ?? false;
                  return (
                    <Button
                      key={opt.id}
                      type="button"
                      variant="outline-solid"
                      size="sm"
                      className={cn(
                        "h-8 shrink-0 rounded-full px-3 text-xs font-medium whitespace-nowrap transition-all duration-200 border shadow-sm",
                        active
                          ? listingFormatFilterChipActiveClasses(opt.id)
                          : listingFormatFilterChipInactiveClasses(opt.id)
                      )}
                      onClick={() => onToggleFormat(opt.id)}
                    >
                      {tFormat(opt.label as any)}
                    </Button>
                  );
                })}
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function DiscoverCategoryMockSection({
  categorySlug,
  name,
  listings,
  currentUserId,
  onBack,
  scrollRootRef,
  compactGutter,
}: {
  categorySlug: string;
  name: string;
  listings: DiscoverMockListing[];
  currentUserId?: string;
  onBack: () => void;
  scrollRootRef?: RefObject<HTMLElement | null>;
  compactGutter?: boolean;
}) {
  const router = useRouter();
  const [sortMode, setSortMode] = useState<CategorySortMode>("recommended");
  const [page, setPage] = useState(1);
  const [isAppending, setIsAppending] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreArmedRef = useRef(true);

  const showEndingSoon = categoryHasAuctions(listings);

  const sorted = useMemo(() => sortDiscoverListings(listings, sortMode), [listings, sortMode]);
  const total = sorted.length;
  const visibleCount = Math.min(page * PAGE_SIZE, total);
  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < total;

  useEffect(() => {
    setPage(1);
    loadMoreArmedRef.current = true;
  }, [sortMode]);

  const loadMore = useCallback(() => {
    if (!hasMore || isAppending) return;
    setIsAppending(true);
    window.setTimeout(() => {
      setPage((p) => p + 1);
      setIsAppending(false);
      loadMoreArmedRef.current = true;
    }, 400);
  }, [hasMore, isAppending]);

  // Re-arm after each reveal: IntersectionObserver only fires on an intersection
  // *transition*, so a sentinel still in view after a page reveals would
  // otherwise stall until the user scrolls up and back down (reported on the
  // discover category panel).
  useEffect(() => {
    loadMoreArmedRef.current = true;
  }, [visibleCount]);

  useEffect(() => {
    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const run = () => {
      if (cancelled) return;
      const node = sentinelRef.current;
      if (!node || !hasMore) return;
      const root = scrollRootRef?.current ?? null;
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (!entry.isIntersecting) {
            loadMoreArmedRef.current = true;
            return;
          }
          if (!loadMoreArmedRef.current) return;
          loadMoreArmedRef.current = false;
          loadMore();
        },
        { root, rootMargin: "240px" }
      );
      observer.observe(node);
    };
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outer);
      window.cancelAnimationFrame(inner);
      observer?.disconnect();
    };
  }, [hasMore, loadMore, visibleCount, scrollRootRef]);

  return (
    <>
      <CategoryHeader
        categorySlug={categorySlug}
        name={name}
        sortMode={sortMode}
        setSortMode={setSortMode}
        showEndingSoon={showEndingSoon}
        onBack={onBack}
        compactGutter={compactGutter}
      />

      <div className={CATEGORY_SHELL}>
        <div className={CATEGORY_GRID}>
          {visible.map((listing) => (
            <div key={listing.id} className="h-auto w-full min-w-0">
              <ItemCard
                listingId={listing.id}
                title={listing.title}
                price={listing.price}
                currency={listing.currency ?? null}
                image_urls={listing.image_urls}
                stock={listing.quantity}
                scheduledPublishAt={listing.scheduled_publish_at as string}
                listingFormat={listing.listing_format ?? null}
                listingType={(listing as { type?: string | null }).type ?? null}
                discoverTypeLabel={listing.discoverTypeLabel}
                currentUserId={currentUserId}
                sellerId={listing.user_id}
                sellerName={listing.sellerUsername}
                sellerAvatarUrl={listing.sellerAvatarUrl ?? null}
                condition={listing.condition}
                cardDetails={listing.card_details ?? null}
                description={listing.description}
                onClick={() => router.push(`/listings/${listing.id}`)}
              />
            </div>
          ))}
        </div>

        <div
          className={cn("flex justify-center py-6", !isAppending && !hasMore && "min-h-0 py-4")}
          aria-hidden={!isAppending && !hasMore}
        >
          {isAppending ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : null}
        </div>
        <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
      </div>
    </>
  );
}

function ProfileCollectionHeaderMiddle({
  collectionId,
  title,
  description,
  isOwner,
  sellerBanned,
  onTitleChange,
  onDescriptionChange,
}: {
  collectionId: string;
  title: string;
  description?: string | null;
  isOwner: boolean;
  sellerBanned: boolean;
  onTitleChange?: (cid: string, nextTitle: string) => Promise<void>;
  onDescriptionChange?: (cid: string, nextDescription: string) => Promise<void>;
}) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const [descriptionDraft, setDescriptionDraft] = useState(description ?? "");

  useEffect(() => {
    setEditingTitle(false);
    setEditingDescription(false);
    setTitleDraft(title);
    setDescriptionDraft(description ?? "");
  }, [collectionId, title, description]);

  const canEdit = isOwner && !sellerBanned;

  const saveTitle = async () => {
    if (!onTitleChange) return;
    const t = titleDraft.trim();
    if (!t) {
      toast.error("Title cannot be empty");
      setTitleDraft(title);
      setEditingTitle(false);
      return;
    }
    if (t === title.trim()) {
      setEditingTitle(false);
      return;
    }
    await onTitleChange(collectionId, t);
    setEditingTitle(false);
  };

  const saveDescription = async () => {
    if (!onDescriptionChange) return;
    const next = descriptionDraft.trim();
    if (next === (description ?? "").trim()) {
      setEditingDescription(false);
      return;
    }
    await onDescriptionChange(collectionId, next);
    setEditingDescription(false);
  };

  return (
    <div className="min-w-0 space-y-1 pr-1">
      <div className="flex items-center gap-1">
        {editingTitle ? (
          <input
            type="text"
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            className="min-w-0 flex-1 border-b border-border bg-transparent text-lg font-semibold tracking-tight text-foreground outline-none focus-visible:ring-0 md:text-2xl lg:text-[1.75rem]"
            maxLength={50}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void saveTitle();
              if (e.key === "Escape") {
                setTitleDraft(title);
                setEditingTitle(false);
              }
            }}
            onBlur={() => void saveTitle()}
            aria-label="Collection title"
          />
        ) : (
          <>
            <h1 className="min-w-0 flex-1 truncate text-lg font-semibold tracking-tight text-foreground drop-shadow-sm md:text-2xl lg:text-[1.75rem]">
              {title}
            </h1>
            {canEdit && onTitleChange ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full"
                onClick={() => {
                  setTitleDraft(title);
                  setEditingTitle(true);
                }}
                aria-label="Edit collection title"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </>
        )}
      </div>
      {(description != null && description !== "") || isOwner ? (
        <div className="pt-0.5">
          {editingDescription ? (
            <div className="inline-flex w-full max-w-full items-center gap-1.5 md:max-w-2xl lg:max-w-3xl">
              <input
                type="text"
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                className="min-w-0 flex-1 border-b border-border bg-transparent text-base text-muted-foreground outline-none focus-visible:ring-0 sm:text-sm"
                maxLength={100}
                placeholder="Add a description..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") void saveDescription();
                  if (e.key === "Escape") {
                    setDescriptionDraft(description ?? "");
                    setEditingDescription(false);
                  }
                }}
                onBlur={() => void saveDescription()}
                aria-label="Collection description"
              />
            </div>
          ) : (
            <div className="inline-flex max-w-full items-start gap-1.5">
              <p className="inline-block max-w-[min(100%,40rem)] wrap-break-word text-xs text-muted-foreground line-clamp-2 sm:text-sm">
                {description ? description : isOwner ? "Add a description…" : ""}
              </p>
              {canEdit && onDescriptionChange ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0 rounded-full"
                  onClick={() => {
                    setDescriptionDraft(description ?? "");
                    setEditingDescription(true);
                  }}
                  aria-label="Edit collection description"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function ProfileCollectionListingsPanel({
  name,
  collectionId,
  collectionDescription,
  listings,
  currentUserId,
  sellerBanned = false,
  isOwner = false,
  onBack,
  scrollRootRef,
  compactGutter,
  onCollectionTitleChange,
  onCollectionDescriptionChange,
  onListingRemovedFromCollection,
  onAddItems,
}: {
  name: string;
  collectionId: string;
  collectionDescription?: string | null;
  listings: ListingItem[];
  currentUserId?: string;
  sellerBanned?: boolean;
  isOwner?: boolean;
  onBack: () => void;
  scrollRootRef?: RefObject<HTMLElement | null>;
  compactGutter?: boolean;
  onCollectionTitleChange?: (collectionId: string, newTitle: string) => Promise<void>;
  onCollectionDescriptionChange?: (collectionId: string, newDescription: string) => Promise<void>;
  onListingRemovedFromCollection?: (collectionId: string, listingId: string) => void;
  onAddItems?: () => void;
}) {
  const tCommon = useTranslations("common");
  const tSort = useTranslations("sort");
  const tTabs = useTranslations("profile.tabs");
  const tML = useTranslations("myListings");
  const router = useRouter();
  const [sortMode, setSortMode] = useState<CategorySortMode>("recommended");
  const [page, setPage] = useState(1);
  const [isAppending, setIsAppending] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{ id: string; title: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreArmedRef = useRef(true);

  const showEndingSoon = liveCategoryHasAuctions(listings);
  const showRemoval = Boolean(isOwner && !sellerBanned && onListingRemovedFromCollection);

  const showAddItems = Boolean(isOwner && !sellerBanned && onAddItems);

  const sorted = useMemo(() => sortLiveDiscoverListings(listings, sortMode), [listings, sortMode]);
  const total = sorted.length;
  const visibleCount = Math.min(page * PAGE_SIZE, total);
  const visible = sorted.slice(0, visibleCount);
  const hasMore = visibleCount < total;

  useEffect(() => {
    setPage(1);
    loadMoreArmedRef.current = true;
  }, [sortMode, listings]);

  const loadMore = useCallback(() => {
    if (!hasMore || isAppending) return;
    setIsAppending(true);
    window.setTimeout(() => {
      setPage((p) => p + 1);
      setIsAppending(false);
      loadMoreArmedRef.current = true;
    }, 400);
  }, [hasMore, isAppending]);

  // Re-arm after each reveal: IntersectionObserver only fires on an intersection
  // *transition*, so a sentinel still in view after a page reveals would
  // otherwise stall until the user scrolls up and back down (reported on the
  // discover category panel).
  useEffect(() => {
    loadMoreArmedRef.current = true;
  }, [visibleCount]);

  useEffect(() => {
    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const run = () => {
      if (cancelled) return;
      const node = sentinelRef.current;
      if (!node || !hasMore) return;
      const root = scrollRootRef?.current ?? null;
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (!entry.isIntersecting) {
            loadMoreArmedRef.current = true;
            return;
          }
          if (!loadMoreArmedRef.current) return;
          loadMoreArmedRef.current = false;
          loadMore();
        },
        { root, rootMargin: "240px" }
      );
      observer.observe(node);
    };
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outer);
      window.cancelAnimationFrame(inner);
      observer?.disconnect();
    };
  }, [hasMore, loadMore, visibleCount, scrollRootRef]);

  const confirmRemoveFromCollection = async () => {
    if (!removeTarget) return;
    setRemoving(true);
    try {
      const res = await fetch("/api/collections/remove-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ collection_id: collectionId, listing_ids: [removeTarget.id] }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        toast.error(
          typeof payload?.error === "string" ? payload.error : tCommon("removeListingFromCollectionError")
        );
        return;
      }
      onListingRemovedFromCollection?.(collectionId, removeTarget.id);
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <CategoryHeader
        categorySlug="collection"
        name={name}
        sortMode={sortMode}
        setSortMode={setSortMode}
        showEndingSoon={showEndingSoon}
        onBack={onBack}
        compactGutter={compactGutter}
        centerMiddleRow={!isOwner && !collectionDescription?.trim()}
        middleContent={
          <ProfileCollectionHeaderMiddle
            collectionId={collectionId}
            title={name}
            description={collectionDescription}
            isOwner={Boolean(isOwner)}
            sellerBanned={Boolean(sellerBanned)}
            onTitleChange={onCollectionTitleChange}
            onDescriptionChange={onCollectionDescriptionChange}
          />
        }
      />

      <div className={CATEGORY_SHELL}>
        {sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <PackageOpen className="h-8 w-8 text-muted-foreground" />
            </div>
            <p className="text-base font-semibold text-foreground">{tTabs("collectionEmpty")}</p>
            {showAddItems ? (
              <Button
                type="button"
                className="mt-4 rounded-full"
                onClick={onAddItems}
              >
                <Plus className="mr-2 h-4 w-4" />
                {tTabs("addListings")}
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            {showAddItems ? (
              <button
                type="button"
                onClick={onAddItems}
                className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-muted-foreground/20 p-3 text-muted-foreground transition-colors hover:border-muted-foreground/40 hover:text-foreground"
              >
                <Plus className="h-4 w-4" />
                <span className="text-[13px] font-medium">{tML("addItemsToCollection")}</span>
              </button>
            ) : null}
          <div className={CATEGORY_GRID}>
            {visible.map((listing) => (
              <div key={listing.id} className="relative h-auto w-full min-w-0">
                {showRemoval ? (
                  <button
                    type="button"
                    className="absolute right-2 top-2 z-40 flex h-8 w-8 items-center justify-center rounded-full bg-background/95 text-foreground shadow-md ring-1 ring-border/80 transition-colors hover:bg-muted"
                    aria-label={tCommon("removeListingFromCollectionAria", { listingTitle: listing.title })}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRemoveTarget({ id: listing.id, title: listing.title });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
                <ItemCard
                  listingId={listing.id}
                  title={listing.title}
                  price={listing.price}
                  currency={listing.currency ?? null}
                  image_urls={listing.image_urls}
                  stock={listing.quantity}
                  scheduledPublishAt={listing.scheduled_publish_at as string}
                  listingFormat={listing.listing_format ?? null}
                  listingType={(listing as { type?: string | null }).type ?? null}
                  discoverTypeLabel={discoverTypeLabelFromListing(listing)}
                  sellerBanned={sellerBanned}
                  currentUserId={currentUserId}
                  sellerId={listing.user_id}
                  sellerName={listing.sellerUsername}
                  sellerAvatarUrl={listing.sellerAvatarUrl ?? null}
                  condition={listing.condition}
                  cardDetails={listing.card_details ?? null}
                  description={listing.description}
                  onClick={() => router.push(`/listings/${listing.id}`)}
                />
              </div>
            ))}
          </div>
          </>
        )}

        <div
          className={cn("flex justify-center py-6", !isAppending && !hasMore && "min-h-0 py-4")}
          aria-hidden={!isAppending && !hasMore}
        >
          {isAppending ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : null}
        </div>
        <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
      </div>

      <AlertDialog open={removeTarget !== null} onOpenChange={(open) => !open && !removing && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tCommon("removeFromCollection")}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription asChild>
              <p>
                {tCommon("removeListingFromCollectionDescription", {
                  listingTitle: removeTarget?.title ?? "",
                  collectionName: name,
                })}
              </p>
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removing}>{tCommon("cancel")}</AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={removing}
              className="min-w-28 rounded-full"
              onClick={() => void confirmRemoveFromCollection()}
            >
              {removing
                ? tCommon("removeListingFromCollectionProgress")
                : tCommon("removeListingFromCollectionAction")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface TcgSubFilterState {
  tcgSets: string[];
  tcgRarities: string[];
  cardConditions: string[];
  cardLanguages: string[];
  gradingCompanies: string[];
  cardGradeRange: [number, number] | null;
}

const EMPTY_TCG_SUB_FILTERS: TcgSubFilterState = {
  tcgSets: [],
  tcgRarities: [],
  cardConditions: [],
  cardLanguages: [],
  gradingCompanies: [],
  cardGradeRange: null,
};

interface TaxonomyOption {
  id: string;
  name: string;
  slug?: string | null;
}

interface GradeScaleOption {
  id: string;
  gradeValue: number;
  gradeLabel: string;
}

function useTcgSubOptions(tcgId: string | null) {
  const [sets, setSets] = useState<TaxonomyOption[]>([]);
  const [rarities, setRarities] = useState<TaxonomyOption[]>([]);
  const [cardConditions, setCardConditions] = useState<TaxonomyOption[]>([]);
  const [languages, setLanguages] = useState<TaxonomyOption[]>([]);
  const [gradingCompanies, setGradingCompanies] = useState<TaxonomyOption[]>([]);

  useEffect(() => {
    if (!tcgId) {
      setSets([]);
      setRarities([]);
      setCardConditions([]);
      setLanguages([]);
      setGradingCompanies([]);
      return;
    }

    let cancelled = false;

    (async () => {
      let setsData, raritiesData, condData, langData, gradingData;
      try {
        [setsData, raritiesData, condData, langData, gradingData] = await Promise.all([
          getTaxonomyItems({ type: "tcg_set", parent: { mode: "eq", id: tcgId }, limit: 200 }),
          getTaxonomyItems({ type: "tcg_rarity", parent: { mode: "eq", id: tcgId }, limit: 200 }),
          getTaxonomyItems({ type: "card_condition", parent: { mode: "root" } }),
          getTaxonomyItems({ type: "card_language", parent: { mode: "root" } }),
          getTaxonomyItems({ type: "grading_company", parent: { mode: "root" } }),
        ]);
      } catch {
        return; // leave sub-options untouched on load failure (prior behavior)
      }

      if (cancelled) return;
      setSets(setsData.map((s) => ({ id: s.id, name: s.name })));
      setRarities(raritiesData.map((r) => ({ id: r.id, name: r.name })));
      setCardConditions(condData.map((c) => ({ id: c.id, name: c.name })));
      setLanguages(langData.map((l) => ({ id: l.id, name: l.name, slug: l.slug })));
      setGradingCompanies(gradingData.map((g) => ({ id: g.id, name: g.name })));
    })();

    return () => { cancelled = true; };
  }, [tcgId]);

  return { sets, rarities, cardConditions, languages, gradingCompanies };
}

function useGradeScales(companyId: string | null) {
  const [scales, setScales] = useState<GradeScaleOption[]>([]);

  useEffect(() => {
    if (!companyId) {
      setScales([]);
      return;
    }
    let cancelled = false;
    const supabase = createClient();

    (async () => {
      const { data } = await supabase
        .from("card_grade_scales")
        .select("id, grade_value, grade_label")
        .eq("company_id", companyId)
        .order("display_order");
      if (cancelled) return;
      setScales(
        (data ?? []).map((r: any) => ({
          id: r.id,
          gradeValue: r.grade_value,
          gradeLabel: r.grade_label,
        }))
      );
    })();

    return () => { cancelled = true; };
  }, [companyId]);

  return scales;
}

interface LegoSubFilterState {
  themes: string[];
  productTypes: string[];
}

function useBuildingBlocksTaxonomyOptions() {
  const [themes, setThemes] = useState<TaxonomyOption[]>([]);
  const [productTypes, setProductTypes] = useState<TaxonomyOption[]>([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let themesData, ptData;
      try {
        [themesData, ptData] = await Promise.all([
          getTaxonomyItems({ type: "theme" }),
          getTaxonomyItems({ type: "product_type" }),
        ]);
      } catch {
        return; // leave options empty on load failure (prior behavior)
      }

      if (cancelled) return;
      setThemes(themesData.map((t) => ({ id: t.id, name: t.name })));
      setProductTypes(ptData.map((p) => ({ id: p.id, name: p.name })));
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { themes, productTypes };
}

function BuildingBlocksContextualFilters({
  state,
  onChange,
}: {
  state: LegoSubFilterState;
  onChange: (next: LegoSubFilterState) => void;
}) {
  const tFilters = useTranslations("feedFilters");
  const tEmpty = useTranslations("emptyState");
  const { themes, productTypes } = useBuildingBlocksTaxonomyOptions();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const chipBtn = (active: boolean) =>
    cn(
      "shrink-0 inline-flex items-center gap-1.5 rounded-full h-8 font-medium transition-all duration-200 border px-2.5 text-xs shadow-sm",
      active
        ? "bg-primary/10 text-primary border-primary"
        : "border-border/40 bg-background/35 text-foreground backdrop-blur-md hover:bg-muted/60 dark:border-white/12 dark:bg-white/5 dark:hover:bg-white/10"
    );

  const toggle = (field: keyof Pick<LegoSubFilterState, "themes" | "productTypes">, id: string) => {
    const prev = state[field];
    const next = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id];
    onChange({ ...state, [field]: next });
  };

  const renderDropdown = (
    key: string,
    label: string,
    options: TaxonomyOption[],
    selected: string[],
    field: keyof Pick<LegoSubFilterState, "themes" | "productTypes">
  ) => {
    if (options.length === 0) return null;
    return (
      <DropdownMenu
        key={key}
        open={openDropdown === key}
        onOpenChange={(open) => setOpenDropdown(open ? key : null)}
      >
        <DropdownMenuTrigger asChild>
          <button type="button" className={chipBtn(selected.length > 0)}>
            <span>{label}</span>
            {selected.length > 0 && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                {selected.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="max-h-[300px] w-56 overflow-y-auto scrollbar-hide">
          {selected.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...state, [field]: [] });
                }}
                className="text-xs font-semibold text-primary hover:text-primary/80"
              >
                {tFilters("clear")}
              </button>
            </div>
          )}
          {options.map((opt) => (
            <DropdownMenuCheckboxItem
              key={opt.id}
              checked={selected.includes(opt.id)}
              onCheckedChange={() => toggle(field, opt.id)}
            >
              {opt.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const hasAny = themes.length > 0 || productTypes.length > 0;
  if (!hasAny) return null;

  return (
    <>
      <div
        className="mx-0.5 h-8 w-px shrink-0 self-center bg-border/40 dark:bg-white/12"
        aria-hidden
      />
      {renderDropdown("legoThemes", tFilters("theme"), themes, state.themes, "themes")}
      {renderDropdown(
        "legoProductTypes",
        tFilters("productType"),
        productTypes,
        state.productTypes,
        "productTypes"
      )}
    </>
  );
}

function TcgContextualFilters({
  tcgId,
  state,
  onChange,
}: {
  tcgId: string;
  state: TcgSubFilterState;
  onChange: (next: TcgSubFilterState) => void;
}) {
  const tTcg = useTranslations("tcgFilters");
  const tFilters = useTranslations("feedFilters");
  const { sets, rarities, cardConditions, languages, gradingCompanies } = useTcgSubOptions(tcgId);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const selectedCompanyId = state.gradingCompanies.length === 1 ? state.gradingCompanies[0] : null;
  const gradeScales = useGradeScales(selectedCompanyId);
  const gradeMin = gradeScales.length > 0 ? Math.min(...gradeScales.map((s) => s.gradeValue)) : 1;
  const gradeMax = gradeScales.length > 0 ? Math.max(...gradeScales.map((s) => s.gradeValue)) : 10;
  const effectiveGradeRange = state.cardGradeRange ?? [gradeMin, gradeMax];

  const toggle = (field: keyof TcgSubFilterState, id: string) => {
    const prev = state[field] as string[];
    const next = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id];
    onChange({ ...state, [field]: next });
  };

  const chipBtn = (active: boolean) =>
    cn(
      "shrink-0 inline-flex items-center gap-1.5 rounded-full h-8 font-medium transition-all duration-200 border px-2.5 text-xs shadow-sm",
      active
        ? "bg-primary/10 text-primary border-primary"
        : "border-border/40 bg-background/35 text-foreground backdrop-blur-md hover:bg-muted/60 dark:border-white/12 dark:bg-white/5 dark:hover:bg-white/10"
    );

  const renderDropdown = (
    key: string,
    label: string,
    options: TaxonomyOption[],
    selected: string[],
    field: keyof TcgSubFilterState,
  ) => {
    if (options.length === 0) return null;
    return (
      <DropdownMenu
        key={key}
        open={openDropdown === key}
        onOpenChange={(open) => setOpenDropdown(open ? key : null)}
      >
        <DropdownMenuTrigger asChild>
          <button className={chipBtn(selected.length > 0)}>
            <span>{label}</span>
            {selected.length > 0 && (
              <span className="flex items-center justify-center w-4 h-4 text-xs rounded-full bg-primary text-primary-foreground">
                {selected.length}
              </span>
            )}
            <ChevronDown className="h-3 w-3" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56 max-h-[300px] overflow-y-auto scrollbar-hide">
          {selected.length > 0 && (
            <div className="flex items-center justify-end px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChange({ ...state, [field]: [] });
                }}
                className="text-xs font-semibold text-primary hover:text-primary/80"
              >
                Clear
              </button>
            </div>
          )}
          {options.map((opt) => {
            const flag = field === "cardLanguages" ? getCardLanguageFlag(opt.slug) : "";
            return (
              <DropdownMenuCheckboxItem
                key={opt.id}
                checked={selected.includes(opt.id)}
                onCheckedChange={() => toggle(field, opt.id)}
              >
                <span className="inline-flex items-center gap-2">
                  {flag ? <span className="text-base leading-none">{flag}</span> : null}
                  <span>{opt.name}</span>
                </span>
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const hasAnyOptions = sets.length > 0 || rarities.length > 0 || cardConditions.length > 0 || languages.length > 0 || gradingCompanies.length > 0;
  if (!hasAnyOptions) return null;

  return (
    <div className="mb-4">
      <div className="scrollbar-hide -mx-1 overflow-x-auto px-1">
        <div className="flex w-max min-w-full gap-2">
          {renderDropdown("sets", tTcg("set"), sets, state.tcgSets, "tcgSets")}
          {renderDropdown("rarities", tTcg("rarity"), rarities, state.tcgRarities, "tcgRarities")}
          {renderDropdown("cardConditions", tTcg("cardCondition"), cardConditions, state.cardConditions, "cardConditions")}
          {renderDropdown("languages", tTcg("language"), languages, state.cardLanguages, "cardLanguages")}
          {renderDropdown("gradingCompanies", tTcg("gradingCompany"), gradingCompanies, state.gradingCompanies, "gradingCompanies")}

          {selectedCompanyId && gradeScales.length > 0 && (
            <DropdownMenu
              open={openDropdown === "gradeRange"}
              onOpenChange={(open) => setOpenDropdown(open ? "gradeRange" : null)}
            >
              <DropdownMenuTrigger asChild>
                <button className={chipBtn(state.cardGradeRange !== null)}>
                  <span>{tTcg("grade")}</span>
                  {state.cardGradeRange && (
                    <span className="text-xs">{state.cardGradeRange[0]}-{state.cardGradeRange[1]}</span>
                  )}
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-[24rem] p-4">
                <GradeRangeFilterPanel
                  value={effectiveGradeRange}
                  min={gradeMin}
                  max={gradeMax}
                  step={0.5}
                  onValueChange={(next) =>
                    onChange({ ...state, cardGradeRange: [next[0], next[1]] })
                  }
                  onClear={() => onChange({ ...state, cardGradeRange: null })}
                  clearLabel="Clear"
                  hint={tFilters("gradeRangeHint")}
                  valueSuffix=""
                  showTickLabels={false}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

function DiscoverCategoryLiveSection({
  categorySlug,
  name,
  initialFilters,
  relativeFilterMode,
  relativeFilterOptions,
  currentUserId,
  onBack,
  scrollRootRef,
  compactGutter,
}: {
  categorySlug: string;
  name: string;
  initialFilters: FeedFilterState;
  relativeFilterMode?: "brand" | "tcg";
  relativeFilterOptions?: RelativeFilterOption[];
  currentUserId?: string;
  onBack: () => void;
  scrollRootRef?: RefObject<HTMLElement | null>;
  compactGutter?: boolean;
}) {
  const tFilters = useTranslations("feedFilters");
  const tEmpty = useTranslations("emptyState");
  const router = useRouter();
  const [sortMode, setSortMode] = useState<CategorySortMode>("recommended");
  const [selectedRelativeIds, setSelectedRelativeIds] = useState<string[]>([]);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);
  const [tcgSubFilters, setTcgSubFilters] = useState<TcgSubFilterState>(EMPTY_TCG_SUB_FILTERS);
  const [legoSubFilters, setLegoSubFilters] = useState<LegoSubFilterState>(() => ({
    themes: initialFilters.themes ?? [],
    productTypes: initialFilters.categories ?? [],
  }));
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreArmedRef = useRef(true);

  useEffect(() => {
    setLegoSubFilters({
      themes: initialFilters.themes ?? [],
      productTypes: initialFilters.categories ?? [],
    });
  }, [initialFilters.themes, initialFilters.categories]);

  const singleSelectedTcgId =
    relativeFilterMode === "tcg" && selectedRelativeIds.length === 1
      ? selectedRelativeIds[0]
      : null;

  const prevTcgIdRef = useRef(singleSelectedTcgId);
  useEffect(() => {
    if (prevTcgIdRef.current !== singleSelectedTcgId) {
      prevTcgIdRef.current = singleSelectedTcgId;
      setTcgSubFilters(EMPTY_TCG_SUB_FILTERS);
    }
  }, [singleSelectedTcgId]);

  const normalizedRelativeOptions = useMemo<RelativeFilterOption[]>(
    () =>
      Array.from(
        new Map((relativeFilterOptions ?? []).map((option) => [option.id, option])).values()
      ),
    [relativeFilterOptions]
  );

  const legoBrandId = useMemo(() => {
    for (const o of normalizedRelativeOptions) {
      const s = o.slug?.trim().toLowerCase();
      if (s === "lego") return o.id;
      if (o.name.trim().toLowerCase() === "lego") return o.id;
    }
    return null;
  }, [normalizedRelativeOptions]);

  const isLegoOnlySelected = useMemo(
    () =>
      legoBrandId !== null &&
      selectedRelativeIds.length === 1 &&
      selectedRelativeIds[0] === legoBrandId,
    [legoBrandId, selectedRelativeIds]
  );

  const prevLegoOnlyRef = useRef(false);
  useEffect(() => {
    if (prevLegoOnlyRef.current && !isLegoOnlySelected) {
      setLegoSubFilters({ themes: [], productTypes: [] });
    }
    prevLegoOnlyRef.current = isLegoOnlySelected;
  }, [isLegoOnlySelected]);

  const scopedFilters = useMemo<FeedFilterState>(() => {
    let base: FeedFilterState = {
      ...initialFilters,
      listingFormats: selectedFormats.length > 0 ? selectedFormats : undefined,
    };

    if (relativeFilterMode === "brand") {
      base = {
        ...base,
        themes: isLegoOnlySelected ? legoSubFilters.themes : [],
        categories: isLegoOnlySelected ? legoSubFilters.productTypes : [],
      };
    }

    if (singleSelectedTcgId) {
      base = {
        ...base,
        tcgSets: tcgSubFilters.tcgSets.length > 0 ? tcgSubFilters.tcgSets : undefined,
        tcgRarities: tcgSubFilters.tcgRarities.length > 0 ? tcgSubFilters.tcgRarities : undefined,
        cardConditions: tcgSubFilters.cardConditions.length > 0 ? tcgSubFilters.cardConditions : undefined,
        cardLanguages: tcgSubFilters.cardLanguages.length > 0 ? tcgSubFilters.cardLanguages : undefined,
        gradingCompanies: tcgSubFilters.gradingCompanies.length > 0 ? tcgSubFilters.gradingCompanies : undefined,
        cardGradeRange: tcgSubFilters.cardGradeRange ?? undefined,
      };
    }

    if (!relativeFilterMode || normalizedRelativeOptions.length === 0) {
      return base;
    }

    const activeIds =
      selectedRelativeIds.length > 0
        ? selectedRelativeIds
        : normalizedRelativeOptions.map((option) => option.id);

    if (relativeFilterMode === "brand") {
      return { ...base, brands: activeIds };
    }

    return { ...base, tcgs: activeIds };
  }, [
    initialFilters,
    isLegoOnlySelected,
    legoSubFilters,
    normalizedRelativeOptions,
    relativeFilterMode,
    selectedRelativeIds,
    selectedFormats,
    singleSelectedTcgId,
    tcgSubFilters,
  ]);

  const {
    items,
    isLoading,
    isInitialized,
    error,
    hasNextPage,
    loadMore,
    refetchFirstPage,
  } = usePaginatedFeed({
    userId: currentUserId,
    mode: "discover",
    filters: scopedFilters,
    limit: PAGE_SIZE,
    maxItems: 2000,
  });

  const sorted = useMemo(() => sortLiveDiscoverListings(items, sortMode), [items, sortMode]);
  const showEndingSoon = liveCategoryHasAuctions(items);
  const isInitialLoading = !isInitialized && isLoading;
  const showBottomSpinner = isLoading && items.length > 0;

  useEffect(() => {
    loadMoreArmedRef.current = true;
  }, [scopedFilters, sortMode]);

  useEffect(() => {
    let cancelled = false;
    let observer: IntersectionObserver | null = null;
    const run = () => {
      if (cancelled) return;
      const node = sentinelRef.current;
      if (!node || !hasNextPage) return;
      const root = scrollRootRef?.current ?? null;
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          if (!entry.isIntersecting) {
            loadMoreArmedRef.current = true;
            return;
          }
          if (!loadMoreArmedRef.current) return;
          loadMoreArmedRef.current = false;
          loadMore();
        },
        { root, rootMargin: "320px" }
      );
      observer.observe(node);
    };
    let inner = 0;
    const outer = window.requestAnimationFrame(() => {
      inner = window.requestAnimationFrame(run);
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(outer);
      window.cancelAnimationFrame(inner);
      observer?.disconnect();
    };
  }, [hasNextPage, loadMore, sorted.length, scrollRootRef]);

  return (
    <>
      <CategoryHeader
        categorySlug={categorySlug}
        name={name}
        sortMode={sortMode}
        setSortMode={setSortMode}
        showEndingSoon={showEndingSoon}
        selectedFormats={selectedFormats}
        onToggleFormat={(id) =>
          setSelectedFormats((prev) =>
            prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
          )
        }
        onBack={onBack}
        compactGutter={compactGutter}
      />

      <div className={CATEGORY_SHELL}>
        {relativeFilterMode && normalizedRelativeOptions.length > 0 && (
          <div className="mb-4">
            <div className="scrollbar-hide -mx-1 overflow-x-auto px-1">
              <div className="flex w-max min-w-full items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant={selectedRelativeIds.length === 0 ? "default" : "outline-solid"}
                  className="h-8 shrink-0 rounded-full px-3 text-xs"
                  onClick={() => setSelectedRelativeIds([])}
                >
                  {tFilters("allCountries")}
                </Button>
                {normalizedRelativeOptions.map((option) => {
                  const active = selectedRelativeIds.includes(option.id);
                  return (
                    <Button
                      key={option.id}
                      type="button"
                      size="sm"
                      variant={active ? "default" : "outline-solid"}
                      className="h-8 shrink-0 rounded-full px-3 text-xs"
                      onClick={() =>
                        setSelectedRelativeIds((prev) =>
                          prev.includes(option.id)
                            ? prev.filter((id) => id !== option.id)
                            : [...prev, option.id]
                        )
                      }
                    >
                      {option.name}
                    </Button>
                  );
                })}
                {relativeFilterMode === "brand" && isLegoOnlySelected ? (
                  <BuildingBlocksContextualFilters state={legoSubFilters} onChange={setLegoSubFilters} />
                ) : null}
              </div>
            </div>
          </div>
        )}

        {singleSelectedTcgId && (
          <TcgContextualFilters
            tcgId={singleSelectedTcgId}
            state={tcgSubFilters}
            onChange={setTcgSubFilters}
          />
        )}

        {error && (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <p className="mb-4 text-sm text-muted-foreground">Could not load listings.</p>
            <Button type="button" variant="secondary" size="sm" className="rounded-full" onClick={() => refetchFirstPage()}>
              Retry
            </Button>
          </div>
        )}

        {!error && isInitialLoading && (
          <ItemCardSkeletonGrid count={12} keyPrefix="discover-category-grid" gridClassName={CATEGORY_GRID} />
        )}

        {!error && isInitialized && !isLoading && sorted.length === 0 && (
          <EmptyState
            title={tEmpty("categoryTitle")}
            description={tEmpty("noListingsDesc")}
          />
        )}

        {!error && sorted.length > 0 && (
          <div className={CATEGORY_GRID}>
            {sorted.map((listing) => (
              <div key={listing.id} className="h-auto w-full min-w-0">
                <ItemCard
                  listingId={listing.id}
                  title={listing.title}
                  price={listing.price}
                  currency={listing.currency ?? null}
                  image_urls={listing.image_urls}
                  stock={listing.quantity}
                  scheduledPublishAt={listing.scheduled_publish_at as string}
                  listingFormat={listing.listing_format ?? null}
                  listingType={(listing as { type?: string | null }).type ?? null}
                  currentUserId={currentUserId}
                  sellerId={listing.user_id}
                  sellerName={listing.sellerUsername}
                  sellerAvatarUrl={listing.sellerAvatarUrl ?? null}
                  condition={listing.condition}
                  cardDetails={listing.card_details ?? null}
                  description={listing.description}
                  onClick={() => router.push(`/listings/${listing.id}`)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex min-h-16 justify-center py-6">
          {showBottomSpinner ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : null}
        </div>
        <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
      </div>
    </>
  );
}

export function DiscoverCategoryPanel({
  categorySlug,
  name,
  mockListings,
  liveBrandId,
  liveFilters,
  relativeFilterMode,
  relativeFilterOptions,
  currentUserId,
  onBack,
  scrollRootRef,
  compactGutter,
}: {
  categorySlug: string;
  name: string;
  mockListings: DiscoverMockListing[];
  liveBrandId?: string | null;
  liveFilters?: FeedFilterState;
  relativeFilterMode?: "brand" | "tcg";
  relativeFilterOptions?: RelativeFilterOption[];
  currentUserId?: string;
  onBack: () => void;
  scrollRootRef?: RefObject<HTMLElement | null>;
  compactGutter?: boolean;
}) {
  if (liveFilters || liveBrandId) {
    const fallbackFilters: FeedFilterState = {
      brands: liveBrandId ? [liveBrandId] : [],
      themes: [],
      categories: [],
      tcgs: [],
      conditions: [],
      gradeRange: null,
    };
    return (
      <DiscoverCategoryLiveSection
        categorySlug={categorySlug}
        name={name}
        initialFilters={liveFilters ?? fallbackFilters}
        relativeFilterMode={relativeFilterMode}
        relativeFilterOptions={relativeFilterOptions}
        currentUserId={currentUserId}
        onBack={onBack}
        scrollRootRef={scrollRootRef}
        compactGutter={compactGutter}
      />
    );
  }
  return (
    <DiscoverCategoryMockSection
      categorySlug={categorySlug}
      name={name}
      listings={mockListings}
      currentUserId={currentUserId}
      onBack={onBack}
      scrollRootRef={scrollRootRef}
      compactGutter={compactGutter}
    />
  );
}
